import { httpClient } from '@shared/infrastructure/http/http-client.js';
import { ownerControlKind, ownerGroupId } from '../lib/group-owner.js';
import { labelStateOf } from '../lib/video-labelling.js';
import type {
  ExecutionVideo,
  ExecutionVideoScore,
  ExecutionVideos,
  GroupOwner,
  GroupSourceType,
  VideoLabel,
} from '../model.js';
import { ownerPath } from './analysis-groups.api.js';

interface ScorePayload {
  outcome?: string | null;
  is_correct?: boolean | null;
  has_false_positive?: boolean | null;
  subtracting_ideas?: number | null;
  overlapping_ideas?: number | null;
  incoherence_ideas?: number | null;
  false_positive_ideas?: number | null;
}

interface ByVideoPayload {
  source_type?: string | null;
  source_id: number | string;
  video_name?: string | null;
  label_type?: string | null;
  run_id?: string | null;
  coherence_status?: string | null;
  methods?: Record<string, ScorePayload | null> | null;
}

interface BucketItemPayload {
  source_type?: string | null;
  source_id: number | string;
  video_name?: string | null;
  label_type?: string | null;
  run_id?: string | null;
  coherence_status?: string | null;
  reason?: string | null;
}

/** The same document `analysisGroupsApi.metrics` reads, this time for its videos. */
interface MetricsPayload {
  scope?: { batch?: string | null; threshold_pct?: number | null } | null;
  byVideo?: ByVideoPayload[] | null;
  buckets?: {
    items?: Partial<
      Record<'evaluated' | 'not_analysed' | 'not_requested', BucketItemPayload[] | null>
    > | null;
  } | null;
}

function toSourceType(raw: string | null | undefined): GroupSourceType {
  return raw === 'interview' ? 'interview' : 'manual_analysis';
}

/** Upstream's word, the single label's or a state's, as the state it means. */
function toLabel(raw: string | null | undefined): VideoLabel | null {
  return labelStateOf(raw);
}

function toScore(raw: ScorePayload | null): ExecutionVideoScore {
  return {
    outcome: raw?.outcome ?? '',
    correct: typeof raw?.is_correct === 'boolean' ? raw.is_correct : null,
    falsePositive: raw?.has_false_positive === true,
    subtractingIdeas: raw?.subtracting_ideas ?? 0,
    overlappingIdeas: raw?.overlapping_ideas ?? null,
    incoherenceIdeas: raw?.incoherence_ideas ?? null,
    falsePositiveIdeas: raw?.false_positive_ideas ?? 0,
  };
}

function toScored(raw: ByVideoPayload): ExecutionVideo {
  return {
    sourceType: toSourceType(raw.source_type),
    sourceId: Number(raw.source_id),
    name: raw.video_name ?? null,
    label: toLabel(raw.label_type),
    runId: raw.run_id ?? null,
    coherenceStatus: raw.coherence_status ?? null,
    reason: null,
    methods: Object.fromEntries(
      Object.entries(raw.methods ?? {}).map(([method, score]) => [method, toScore(score)]),
    ),
  };
}

function toLeftOut(raw: BucketItemPayload): ExecutionVideo {
  return {
    sourceType: toSourceType(raw.source_type),
    sourceId: Number(raw.source_id),
    name: raw.video_name ?? null,
    label: toLabel(raw.label_type),
    runId: raw.run_id ?? null,
    coherenceStatus: raw.coherence_status ?? null,
    reason: raw.reason ?? null,
    methods: {},
  };
}

const keyOf = (video: Pick<ExecutionVideo, 'sourceType' | 'sourceId'>): string =>
  `${video.sourceType}:${video.sourceId}`;

/**
 * Every video of the execution once: the ones upstream scored, as it scored them, and then the
 * ones it only accounts for — no label, a run that failed or still runs, coherence not run.
 */
function toVideos(payload: MetricsPayload): ExecutionVideo[] {
  const scored = (payload.byVideo ?? []).map(toScored);
  const seen = new Set(scored.map(keyOf));
  const items = payload.buckets?.items ?? {};
  const leftOut = [
    ...(items.not_analysed ?? []),
    ...(items.not_requested ?? []),
    ...(items.evaluated ?? []),
  ]
    .map(toLeftOut)
    .filter((video) => {
      const key = keyOf(video);
      if (seen.has(key)) return false;
      seen.add(key);

      return true;
    });

  return [...scored, ...leftOut];
}

/**
 * Boundary with one execution's videos: which ones it ran and how each came out. Upstream's
 * metrics of that execution already carry it (`byVideo`, `buckets`), so nothing new is asked of it:
 * a deleted video is not in them, and neither is one the execution never ran.
 */
export const executionVideosApi = {
  /** An execution the group never ran answers `VP_GROUP_BATCH_NOT_FOUND`. */
  list: async (owner: GroupOwner, batch: string): Promise<ExecutionVideos> => {
    const payload = await httpClient.get<MetricsPayload>(
      `${ownerPath(owner)}/metrics?batch=${encodeURIComponent(batch)}`,
    );

    return {
      groupId: ownerGroupId(owner),
      controlKind: ownerControlKind(owner),
      batch: payload.scope?.batch ?? null,
      thresholdPct:
        typeof payload.scope?.threshold_pct === 'number' ? payload.scope.threshold_pct : null,
      videos: toVideos(payload ?? {}),
    };
  },
};
